/**
 * Sets which agency roles each onboarding archetype grants.
 *
 * Onboarding asks one question -- what does this person do here -- and derives
 * the roles. But two of those roles are hand-made per environment: they exist in
 * production because somebody created them in Roles & Permissions, and no
 * migration owns them. A constant cannot be right everywhere, so this is where
 * an agency says which of its own roles an archetype should grant.
 *
 * Two things are deliberately visible rather than editable. The roles that
 * decide whether somebody can be scheduled are shown locked, because making
 * those configurable would recreate the bug this work exists to remove, one
 * level up. And each archetype says whether it is using the shipped default or
 * an override, because once the mapping is data rather than code, "why did this
 * person get that role" stops being answerable by reading the source.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Checkbox,
} from '@roger/ui-components';
import { AlertTriangle, Loader2, Lock, X } from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';
import { useStore } from '../../store';
import { listRoles } from '../../services/api';
import type { RoleResponse, UserArchetype } from '../../services/api';
import {
  getAgencyConfig,
  updateAgencyConfig,
} from '../../services/agencyConfig';
import {
  ARCHETYPE_GUARANTEED_ROLES,
  ARCHETYPE_OPTIONS,
} from '../clinician-manager/onboarding';
import { filterUserVisibleRoles } from '../../utils/rbacRoles';
import {
  getBusinessErrorMessage,
  getSurfaceableError,
} from '../../utils/apiErrorDetail';
import { hasArchetypeRoleEdits, unresolvedSlugs } from './archetypeRolesConfig';

type SlugsByArchetype = Record<string, string[]>;

export const ArchetypeRolesSection: React.FC = () => {
  const { showSuccess, showError } = useStore(
    useShallow(state => ({
      showSuccess: state.showSuccess,
      showError: state.showError,
    }))
  );

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [agencyRoles, setAgencyRoles] = useState<RoleResponse[]>([]);
  /** What the server has stored; null means every archetype uses its default. */
  const [stored, setStored] = useState<SlugsByArchetype | null>(null);
  /** What each archetype grants once defaults and configuration are resolved. */
  const [effective, setEffective] = useState<SlugsByArchetype>({});
  /** Local edits, keyed by archetype. */
  const [draft, setDraft] = useState<SlugsByArchetype>({});

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const [config, rolesRes] = await Promise.all([
        getAgencyConfig(),
        listRoles({ query: { limit: 500 }, throwOnError: true }),
      ]);
      const storedRoles = (config.archetype_roles ?? null) as Record<
        string,
        { agency_role_slugs?: string[] }
      > | null;
      const flattened: SlugsByArchetype = {};
      for (const [archetype, entry] of Object.entries(storedRoles ?? {})) {
        flattened[archetype] = entry?.agency_role_slugs ?? [];
      }
      setStored(storedRoles ? flattened : null);
      setEffective(
        (config.effective_archetype_roles ?? {}) as SlugsByArchetype
      );
      // Edits start from what is actually in force, so saving without touching
      // an archetype cannot change it.
      setDraft({ ...(config.effective_archetype_roles ?? {}) });
      // Only roles the save endpoint will accept: this agency's own,
      // agency-scoped, non-internal. listRoles also returns shared system and
      // internal roles, and offering one as a checkbox leads nowhere except a
      // 422 the operator cannot act on.
      setAgencyRoles(
        filterUserVisibleRoles(
          (rolesRes.data ?? []).filter(
            role => role.scope === 'agency' && role.is_internal !== true
          )
        )
      );
    } catch (err) {
      console.error('Failed to load archetype role configuration:', err);
      showError('Unable to load onboarding role configuration.');
    } finally {
      setIsLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    load();
  }, [load]);

  const knownSlugs = useMemo(
    () => new Set(agencyRoles.map(role => role.slug)),
    [agencyRoles]
  );

  /**
   * Slugs an archetype grants that this agency has no role for.
   *
   * These are the interesting ones. The shipped defaults name roles somebody
   * made by hand in production, so a fresh agency resolves none of them -- and
   * the save is refused wholesale until they are cleared, which is impossible
   * from a list of checkboxes that cannot show them.
   */
  const unresolvedFor = useCallback(
    (archetype: UserArchetype) => unresolvedSlugs(draft[archetype], knownSlugs),
    [draft, knownSlugs]
  );

  const isOverridden = useCallback(
    (archetype: UserArchetype) => stored !== null && archetype in stored,
    [stored]
  );

  const isDirty = useMemo(
    () =>
      hasArchetypeRoleEdits(
        ARCHETYPE_OPTIONS.map(option => option.value),
        effective,
        draft
      ),
    [draft, effective]
  );

  const toggleRole = useCallback((archetype: UserArchetype, slug: string) => {
    setDraft(previous => {
      const current = previous[archetype] ?? [];
      return {
        ...previous,
        [archetype]: current.includes(slug)
          ? current.filter(s => s !== slug)
          : [...current, slug],
      };
    });
  }, []);

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    try {
      const payload = Object.fromEntries(
        ARCHETYPE_OPTIONS.map(option => [
          option.value,
          { agency_role_slugs: draft[option.value] ?? [] },
        ])
      );
      const updated = await updateAgencyConfig({ archetype_roles: payload });
      setEffective(
        (updated.effective_archetype_roles ?? {}) as SlugsByArchetype
      );
      showSuccess('Onboarding roles updated');
      await load();
    } catch (err) {
      console.error('Failed to save archetype role configuration:', err);
      // The server names the role it could not find, which is exactly what the
      // person configuring needs to see -- it is flagged surfaceable for that
      // reason, so prefer it over the generic 4xx path.
      showError(
        getSurfaceableError(err)?.message ??
          getBusinessErrorMessage(err) ??
          'Failed to save onboarding roles. Please try again.'
      );
    } finally {
      setIsSaving(false);
    }
  }, [draft, load, showSuccess, showError]);

  const handleReset = useCallback(async () => {
    setIsSaving(true);
    try {
      await updateAgencyConfig({ archetype_roles: null });
      showSuccess('Onboarding roles returned to defaults');
      await load();
    } catch (err) {
      console.error('Failed to clear archetype role configuration:', err);
      showError(
        getBusinessErrorMessage(err) ?? 'Failed to reset. Please try again.'
      );
    } finally {
      setIsSaving(false);
    }
  }, [load, showSuccess, showError]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Onboarding roles</CardTitle>
        <CardDescription>
          Which roles each kind of team member gets when they&apos;re added.
          Roles that decide whether someone can be scheduled are fixed and shown
          for reference.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className='flex items-center gap-2 text-sm text-muted-foreground'>
            <Loader2 className='h-4 w-4 animate-spin' />
            Loading…
          </div>
        ) : (
          <div className='space-y-6'>
            {ARCHETYPE_OPTIONS.map(option => (
              <div key={option.value} className='space-y-2'>
                <div className='flex flex-wrap items-center gap-2'>
                  <h4 className='text-sm font-semibold'>{option.label}</h4>
                  <Badge
                    variant='outline'
                    className='whitespace-nowrap text-xs'
                  >
                    {isOverridden(option.value)
                      ? 'Set for this agency'
                      : 'Using defaults'}
                  </Badge>
                </div>

                <div className='flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground'>
                  <Lock className='h-3 w-3' aria-hidden='true' />
                  <span>Always:</span>
                  {ARCHETYPE_GUARANTEED_ROLES[option.value].map(
                    (name: string) => (
                      <Badge
                        key={name}
                        variant='secondary'
                        className='whitespace-nowrap text-xs'
                      >
                        {name}
                      </Badge>
                    )
                  )}
                </div>

                {unresolvedFor(option.value).length > 0 && (
                  <div className='flex flex-wrap items-center gap-1.5 text-xs text-amber-700 dark:text-amber-500'>
                    <AlertTriangle className='h-3 w-3' aria-hidden='true' />
                    <span>No such role in this agency:</span>
                    {unresolvedFor(option.value).map(slug => (
                      <Button
                        key={slug}
                        variant='outline'
                        size='sm'
                        className='h-6 gap-1 px-2 text-xs'
                        disabled={isSaving}
                        onClick={() => toggleRole(option.value, slug)}
                      >
                        {slug}
                        <X className='h-3 w-3' aria-hidden='true' />
                      </Button>
                    ))}
                  </div>
                )}

                <div className='flex flex-wrap gap-3'>
                  {agencyRoles.length === 0 ? (
                    <p className='text-xs text-muted-foreground'>
                      This agency has no agency-level roles to choose from.
                    </p>
                  ) : (
                    agencyRoles.map(role => (
                      <label
                        key={role.id}
                        className='flex items-center gap-2 text-sm'
                      >
                        <Checkbox
                          checkboxSize='sm'
                          checked={(draft[option.value] ?? []).includes(
                            role.slug
                          )}
                          disabled={isSaving}
                          onCheckedChange={() =>
                            toggleRole(option.value, role.slug)
                          }
                        />
                        <span className='whitespace-nowrap'>{role.name}</span>
                      </label>
                    ))
                  )}
                </div>
              </div>
            ))}

            <p className='flex items-start gap-2 text-xs text-muted-foreground'>
              <AlertTriangle className='mt-0.5 h-3.5 w-3.5 shrink-0' />
              <span>
                Saving is refused while any role above is missing, rather than
                silently granting nothing later. Click one to drop it.
              </span>
            </p>

            <div className='flex flex-wrap justify-end gap-2'>
              <Button
                variant='outline'
                onClick={handleReset}
                disabled={isSaving || stored === null}
              >
                Reset to defaults
              </Button>
              <Button onClick={handleSave} disabled={isSaving || !isDirty}>
                {isSaving && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
                Save
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ArchetypeRolesSection;
